import { type FC, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { getIsWithWidgets, getLinePageAnalyticsEvents, getSegmentScoresEntries, getWidgetsTabs, isHideSportComponent, isMatchWidgetTab, selectUserIsAuthenticated, useLineBehaviorV2 } from '@mwl/core-lib';
import { SportButtonFavorites } from '@/components/SportButtonFavorites/SportButtonFavorites';
import { routes } from '@/constants';
import { useSportSidebarBehavior, useTypedSelector } from '@/hooks';
import { widgetsIcons } from '../../LinePage.constants';
import { MatchScores } from '../MatchScores/MatchScores';
import { MatchWidget } from '../MatchWidget/MatchWidget';
import { MatchWidgetTabs } from '../MatchWidgetTabs/MatchWidgetTabs';
import { Separator } from '../Separator/Separator';
import type { MatchInfoDesktopProps } from './MatchInfoDesktop.types';
import styles from './MatchInfoDesktop.module.scss';
export const MatchInfoDesktop: FC<MatchInfoDesktopProps> = ({
  renderCompetitors,
  line,
  className,
  isFavorite
}) => {
  const widgetsRef = useRef<HTMLDivElement | null>(null);
  const router = useRouter();
  const [widgetOffsetHeight, setWidgetOffsetHeight] = useState<number | null>(null);
  const isAuth = useTypedSelector(selectUserIsAuthenticated);
  const {
    hasTranslation
  } = useLineBehaviorV2({
    line,
    routes
  });
  const isWithWidgets = getIsWithWidgets(line, false, hasTranslation);
  const {
    isTabletScreen
  } = useSportSidebarBehavior();
  const isRenderScore = Boolean(!isHideSportComponent({
    sportCode: line.sport.code,
    stage: line.stage,
    componentName: 'matchScores'
  }) && (isWithWidgets && widgetOffsetHeight || !isWithWidgets));
  const {
    stage
  } = line;
  const analyticsData = getLinePageAnalyticsEvents(router.asPath, line);
  const widgetsTabs = getWidgetsTabs({
    hasTranslation,
    hasWidgets: false,
    stage,
    iconMap: widgetsIcons
  });
  const segmentedScores = getSegmentScoresEntries(line);
  useEffect(() => {
    const currentWidgetsRef = widgetsRef.current;
    let resizeObserver: ResizeObserver;
    if (currentWidgetsRef) {
      resizeObserver = new ResizeObserver(() => {
        setWidgetOffsetHeight(currentWidgetsRef.offsetHeight);
      });
      resizeObserver.observe(currentWidgetsRef);
    }
    return () => {
      resizeObserver?.disconnect();
    };
  }, []);
  return <div className={cn(styles.root, className)} data-sentry-component="MatchInfoDesktop" data-sentry-source-file="MatchInfoDesktop.tsx">
      <div className={styles.stats} style={isTabletScreen ? undefined : {
      height: !widgetOffsetHeight ? 'auto' : widgetOffsetHeight
    }}>
        {renderCompetitors()}

        {line.stage === 'LIVE' && <Separator className={styles.separator} />}

        {isRenderScore && <MatchScores className={styles.scores} segmentedScores={segmentedScores} sportCode={line.sport.code} />}
        {isAuth && <div className={cn(styles.favorites, {
        [styles.active]: isFavorite
      })}>
            <SportButtonFavorites size="xs" lineId={line.id} isFavorite={isFavorite} analyticsAddFavorites={analyticsData.addFavorite} analyticsDeleteFavorites={analyticsData.delFavorite} />
          </div>}
      </div>

      {isWithWidgets && <div className={styles.widgets} ref={widgetsRef}>
          <MatchWidgetTabs tabs={widgetsTabs} hasTranslation={hasTranslation} className={styles.widgetTabs}>
            {activeTab => isMatchWidgetTab(activeTab) && <MatchWidget line={line} activeTab={activeTab} />}
          </MatchWidgetTabs>
        </div>}
    </div>;
};